import React from "react"
import { createRoot } from "react-dom/client"
import { Controller } from "@hotwired/stimulus"
import application from "stimulus_init"
import whenTCInitialized from "lib/whenTCInitialized"

// See rails/doc/react-mount.md for registration and bundle-loading guidance
const reactMount =
  application.reactMount ||
  (application.reactMount = {
    components: new Map(),
    registeredControllers: new Set(),
  })

const componentNamePattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

// Helper to ensure that the component name is a string and is in kebab case format
export const controllerIdentifierFor = (name) => {
  if (typeof name !== "string") {
    throw new TypeError("React component name must be a string.")
  }

  if (!componentNamePattern.test(name)) {
    throw new Error(`React component names must be kebab-case: '${name}'.`)
  }

  return `react-mount-${name}`
}

const ensureControllerRegistered = (identifier) => {
  if (reactMount.registeredControllers.has(identifier)) return

  application.register(identifier, ReactMountController)
  reactMount.registeredControllers.add(identifier)
}

// Load and register the component only when its mount, or an explicitly provided
// load trigger, exists. The trigger is useful when a mount will be inserted later
// into an existing container.
export const registerComponent = async (name, loadComponent, loadWhenId) => {
  const identifier = controllerIdentifierFor(name)

  const loadTrigger = loadWhenId
    ? document.getElementById(loadWhenId)
    : document.querySelector(`[data-controller~="${identifier}"]`)

  if (!loadTrigger) return

  const { default: component } = await loadComponent()

  return registerComponentEagerly(name, component)
}

// Register an already-loaded component after page initialization
export const registerComponentEagerly = async (name, component) => {
  const identifier = controllerIdentifierFor(name)

  await whenTCInitialized()

  const registeredComponent = reactMount.components.get(name)

  if (registeredComponent && registeredComponent !== component) {
    throw new Error(`React component '${name}' is already registered.`)
  }

  reactMount.components.set(name, component)
  ensureControllerRegistered(identifier)
}

export class ReactMountController extends Controller {
  static targets = ["mount", "props"]

  static values = {
    component: String,
  }

  connect() {
    this.resolveComponent()
    this.root = createRoot(this.mountTarget)
    this.render()

    this.propsObserver = new MutationObserver(() => this.render())
    this.propsObserver.observe(this.propsTarget, {
      characterData: true,
      childList: true,
      subtree: true,
    })

    this.boundHandleBeforeMorph ||= this.handleBeforeMorph.bind(this)
    this.element.addEventListener("turbo:before-morph-element", this.boundHandleBeforeMorph)
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-morph-element", this.boundHandleBeforeMorph)
    this.propsObserver?.disconnect()
    this.root?.unmount()
    this.propsObserver = undefined
    this.root = undefined
  }

  render() {
    const component = this.resolveComponent()

    const props = JSON.parse(this.propsTarget.textContent || "{}")
    this.root.render(React.createElement(component, props))
  }

  resolveComponent() {
    const component = reactMount.components.get(this.componentValue)

    if (!component) {
      throw new Error(`Unknown React component: ${this.componentValue}`)
    }

    return component
  }

  handleBeforeMorph(event) {
    if (event.target !== this.element) return

    const newElement = event.detail.newElement
    if (!(newElement instanceof Element)) return

    const controllerNames = newElement.getAttribute("data-controller")?.split(/\s+/) || []
    if (!controllerNames.includes(this.identifier)) return

    const componentAttribute = `data-${this.identifier}-component-value`
    const targetAttribute = `data-${this.identifier}-target`
    const componentName = newElement.getAttribute(componentAttribute)
    if (!componentName) return

    const newPropsTarget = newElement.querySelector(`[${targetAttribute}~='props']`)
    if (!newPropsTarget) return

    this.element.setAttribute(componentAttribute, componentName)
    this.propsTarget.textContent = newPropsTarget.textContent || "{}"
    event.preventDefault()
  }
}
